import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { EASTERN_TIMEZONE } from '../../helpers/helpers';
import './multiDateCalendar.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MultiDateCalendar = ({
  selectedDates = {},
  onToggleDate,
  onRemoveDate,
  allowPastDates = false,
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));

  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = endOfMonth.date();

    const days = [];

    // Leading empty cells for alignment
    for (let i = 0; i < startDay; i++) {
      days.push({ key: `empty-${i}`, empty: true });
    }

    // Actual days
    const todayEastern = dayjs().tz(EASTERN_TIMEZONE).startOf('day');
    for (let d = 1; d <= daysInMonth; d++) {
      const date = currentMonth.date(d);
      const dateStr = date.format('YYYY-MM-DD');
      const isToday = date.isSame(todayEastern, 'day');
      const isPast = date.isBefore(todayEastern, 'day');
      const isSelected = !!selectedDates[dateStr];
      days.push({
        key: dateStr,
        date,
        dateStr,
        day: d,
        isToday,
        isPast,
        isSelected,
      });
    }

    return days;
  }, [currentMonth, selectedDates]);

  const sortedSelectedDates = useMemo(() => {
    return Object.keys(selectedDates).sort();
  }, [selectedDates]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.add(1, 'month'));
  };

  const handleDayClick = dayInfo => {
    if (dayInfo.empty || (dayInfo.isPast && !allowPastDates)) return;
    onToggleDate(dayInfo.dateStr);
  };

  return (
    <div className="mdc-root">
      {/* Calendar grid */}
      <div className="mdc-calendar">
        {/* Month navigation */}
        <div className="mdc-header">
          <button
            type="button"
            className="mdc-nav-btn"
            onClick={handlePrevMonth}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 12L6 8L10 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span className="mdc-month-label">
            {currentMonth.format('MMMM YYYY')}
          </span>
          <button
            type="button"
            className="mdc-nav-btn"
            onClick={handleNextMonth}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Weekday headers */}
        <div className="mdc-weekdays">
          {WEEKDAYS.map(day => (
            <div key={day} className="mdc-weekday">
              {day}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="mdc-grid">
          {calendarDays.map(dayInfo => (
            <button
              key={dayInfo.key}
              type="button"
              disabled={dayInfo.empty || (dayInfo.isPast && !allowPastDates)}
              className={[
                'mdc-day',
                dayInfo.empty && 'mdc-day--empty',
                dayInfo.isPast && 'mdc-day--past',
                dayInfo.isToday && 'mdc-day--today',
                dayInfo.isSelected && 'mdc-day--selected',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleDayClick(dayInfo)}
            >
              {!dayInfo.empty && dayInfo.day}
            </button>
          ))}
        </div>
      </div>

      {/* Selected dates summary */}
      {sortedSelectedDates.length > 0 && (
        <div className="mdc-selection">
          <div className="mdc-selection-header">
            <span className="mdc-selection-count">
              {sortedSelectedDates.length} day
              {sortedSelectedDates.length !== 1 ? 's' : ''} selected
            </span>
            <button
              type="button"
              className="mdc-clear-btn"
              onClick={() => sortedSelectedDates.forEach(d => onRemoveDate(d))}
            >
              Clear all
            </button>
          </div>
          <div className="mdc-chips">
            {sortedSelectedDates.map(date => (
              <span
                key={date}
                className="mdc-chip"
                onClick={() => onRemoveDate(date)}
                title="Click to remove"
              >
                {dayjs(date).format('ddd, MMM D')}
                <svg
                  className="mdc-chip-x"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M9 3L3 9M3 3L9 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiDateCalendar;
